// SHADOW NETWORK — Image Lightbox (additive).
// Provides a global fullscreen image zoom overlay. Any component can call
// `useLightbox().open(src, caption)` to display an image. Closes on ESC,
// backdrop click, image click, or [×] button.
//
// Wrap the app once with <ImageLightboxProvider> (see App.js). The overlay
// renders at the very top of the React tree so it always sits above modals.
import React, { createContext, useCallback, useContext, useEffect, useState } from "react";

const LightboxContext = createContext({ open: () => {}, close: () => {} });

export const useLightbox = () => useContext(LightboxContext);

export const ImageLightboxProvider = ({ children }) => {
  const [state, setState] = useState({ open: false, src: "", caption: "" });

  const open = useCallback((src, caption = "") => {
    if (!src) return;
    setState({ open: true, src, caption });
  }, []);

  const close = useCallback(() => {
    setState((s) => ({ ...s, open: false }));
    // Delay clearing src so the exit animation can play.
    setTimeout(() => setState({ open: false, src: "", caption: "" }), 360);
  }, []);

  useEffect(() => {
    if (!state.open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => {
      if (e.key === "Escape") close();
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener("keydown", onKey);
    };
  }, [state.open, close]);

  return (
    <LightboxContext.Provider value={{ open, close }}>
      {children}
      <ImageLightboxOverlay state={state} close={close} />
    </LightboxContext.Provider>
  );
};

const ImageLightboxOverlay = ({ state, close }) => (
  <div
    className={`img-lb-wrap${state.open ? " open" : ""}`}
    aria-hidden={!state.open}
    role="dialog"
    data-testid="image-lightbox"
  >
    <div className="img-lb-backdrop" onClick={close} />
    <button className="img-lb-close" onClick={close} aria-label="Cerrar" data-testid="image-lightbox-close">
      ×
    </button>
    <div className="img-lb-content">
      {state.src && (
        <img
          src={state.src}
          alt={state.caption || "lightbox"}
          className="img-lb-img"
          onClick={close}
        />
      )}
    </div>
    {state.caption && <div className="img-lb-caption">{state.caption}</div>}
  </div>
);

export default ImageLightboxProvider;
